import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Chip,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Tabs,
  Tab,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close,
  Search,
  TrendingUp,
  TrendingDown,
  FilterList,
  CalendarToday,
  Receipt,
} from '@mui/icons-material';
import { Transaction, LoyaltyProgram } from '../../types';
import { format, isWithinInterval, startOfMonth, endOfMonth, subMonths } from 'date-fns';

interface TransactionHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  program: LoyaltyProgram;
  transactions: Transaction[];
}

type FilterType = 'all' | 'earned' | 'redeemed' | '30days' | '90days';

const TransactionHistoryDialog: React.FC<TransactionHistoryDialogProps> = ({
  open,
  onClose,
  program,
  transactions,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const theme = useTheme();

  // Debug logging
  React.useEffect(() => {
    if (open) {
      console.log('═══════════════════════════════════════════');
      console.log('📊 TRANSACTION HISTORY DIALOG - OPENED');
      console.log('═══════════════════════════════════════════');
      console.log('🔖 Program:', program.name, '(ID:', program.id + ')');
      console.log('📦 Total transactions received:', transactions.length);
      console.log('📋 All transactions:', transactions.map(t => ({
        id: t.id,
        programId: t.programId,
        description: t.description,
        points: t.points,
        type: t.type,
        date: t.date
      })));
    }
  }, [open, program, transactions]);

  // Filter transactions for this program
  const programTransactions = transactions.filter(t => t.programId === program.id);

  // Debug filtered transactions
  React.useEffect(() => {
    if (open) {
      console.log('🔍 Filtered for program:', programTransactions.length, 'transactions');
      console.log('🔍 Program transactions:', programTransactions.map(t => ({
        id: t.id,
        description: t.description,
        points: t.points
      })));
    }
  }, [open, programTransactions]);

  // Apply filters
  const filteredTransactions = programTransactions.filter(txn => {
    // Search filter
    if (searchQuery && !txn.description.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    // Type filter
    if (filter === 'earned' && txn.type !== 'earned') return false;
    if (filter === 'redeemed' && txn.type !== 'redeemed') return false;

    // Date filters
    const now = new Date();
    if (filter === '30days') {
      const thirtyDaysAgo = subMonths(now, 1);
      if (txn.date < thirtyDaysAgo) return false;
    }
    if (filter === '90days') {
      const ninetyDaysAgo = subMonths(now, 3);
      if (txn.date < ninetyDaysAgo) return false;
    }

    return true;
  });

  // Calculate statistics
  const totalEarned = programTransactions
    .filter(t => t.type === 'earned')
    .reduce((sum, t) => sum + t.points, 0);
  
  const totalRedeemed = programTransactions
    .filter(t => t.type === 'redeemed')
    .reduce((sum, t) => sum + Math.abs(t.points), 0);

  const last30DaysEarned = programTransactions
    .filter(t => t.type === 'earned' && t.date >= subMonths(new Date(), 1))
    .reduce((sum, t) => sum + t.points, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Receipt sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {program.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Transaction History
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Statistics Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
          <Card 
            elevation={0}
            sx={{ 
              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
              bgcolor: alpha(theme.palette.success.main, 0.05),
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Total Earned
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight={700} color="success.main">
                {totalEarned.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>

          <Card 
            elevation={0}
            sx={{ 
              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
              bgcolor: alpha(theme.palette.error.main, 0.05),
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Total Redeemed
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight={700} color="error.main">
                {totalRedeemed.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>

          <Card 
            elevation={0}
            sx={{ 
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CalendarToday sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Last 30 Days
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                +{last30DaysEarned.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Search and Filters */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label="All"
              onClick={() => setFilter('all')}
              color={filter === 'all' ? 'primary' : 'default'}
              size="small"
              sx={{ fontWeight: filter === 'all' ? 600 : 400 }}
            />
            <Chip
              label="Earned"
              onClick={() => setFilter('earned')}
              color={filter === 'earned' ? 'success' : 'default'}
              size="small"
              sx={{ fontWeight: filter === 'earned' ? 600 : 400 }}
            />
            <Chip
              label="Redeemed"
              onClick={() => setFilter('redeemed')}
              color={filter === 'redeemed' ? 'error' : 'default'}
              size="small"
              sx={{ fontWeight: filter === 'redeemed' ? 600 : 400 }}
            />
            <Chip
              label="Last 30 Days"
              onClick={() => setFilter('30days')}
              color={filter === '30days' ? 'primary' : 'default'}
              size="small"
              sx={{ fontWeight: filter === '30days' ? 600 : 400 }}
            />
            <Chip
              label="Last 90 Days"
              onClick={() => setFilter('90days')}
              color={filter === '90days' ? 'primary' : 'default'}
              size="small"
              sx={{ fontWeight: filter === '90days' ? 600 : 400 }}
            />
          </Box>
        </Box>

        {/* Transaction List */}
        {filteredTransactions.length > 0 ? (
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {filteredTransactions
              .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
              .map((txn, index) => (
                <Card
                  key={txn.id || index}
                  elevation={0}
                  sx={{
                    mb: 1.5,
                    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    borderLeft: `4px solid ${txn.type === 'earned' ? '#10b981' : '#ef4444'}`,
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 12px rgba(0, 0, 0, 0.3)'
                        : '0 4px 12px rgba(0, 0, 0, 0.1)',
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} gutterBottom>
                          {txn.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(txn.date), 'MMM dd, yyyy')}
                          </Typography>
                          <Chip
                            label={txn.type}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              bgcolor: txn.type === 'earned' ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1),
                              color: txn.type === 'earned' ? '#10b981' : '#ef4444',
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: txn.type === 'earned' ? '#10b981' : '#ef4444',
                          fontWeight: 700,
                        }}
                      >
                        {txn.type === 'earned' ? '+' : '-'}
                        {Math.abs(txn.points).toLocaleString()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
          </Box>
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              px: 3,
              borderRadius: 3,
              border: `2px dashed ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            }}
          >
            <Typography variant="body2" color="text.secondary" gutterBottom>
              No transactions found
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {searchQuery ? 'Try a different search term' : 'Add your first transaction to get started'}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionHistoryDialog;
